
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Input } from "@/components/ui/input";

const Verify = () => {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const phoneNumber = location.state?.phoneNumber;
  const mode = location.state?.mode || 'signup'; // Default to signup for backward compatibility

  useEffect(() => {
    if (!phoneNumber) {
      navigate('/login', {
        replace: true
      });
    }
  }, [phoneNumber, navigate]);
  useEffect(() => {
    let timer: number;
    if (cooldownTime > 0) {
      timer = window.setInterval(() => {
        setCooldownTime(time => time - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [cooldownTime]);
  const handleResendCode = async () => {
    if (cooldownTime > 0) return;
    try {
      if (!phoneNumber) {
        toast.error('Phone number not found. Please try again.');
        navigate('/login', {
          replace: true
        });
        return;
      }
      
      // Get firstName and lastName from localStorage if in signup mode
      const pendingSignupStr = mode === 'signup' ? localStorage.getItem('pendingSignup') : null;
      const pendingSignup = pendingSignupStr ? JSON.parse(pendingSignupStr) : null;
      
      // Pass them to the OTP function
      const {
        error
      } = await supabase.auth.signInWithOtp({
        phone: phoneNumber,
        options: {
          shouldCreateUser: mode === 'signup',
          data: pendingSignup ? {
            firstName: pendingSignup.firstName,
            lastName: pendingSignup.lastName
          } : undefined
        }
      });
      if (error) throw error;
      toast.success('Verification code resent!');
      setCooldownTime(30);
    } catch (error: any) {
      console.error('Error resending code:', error);
      const errorMessage = error.message || error.error_description || 'Failed to resend verification code';
      toast.error(errorMessage);
    }
  };
  const handleVerify = async () => {
    if (!phoneNumber) {
      toast.error('Phone number not found. Please try again.');
      navigate('/login', {
        replace: true
      });
      return;
    }
    setIsLoading(true);
    try {
      // First verify the OTP
      const {
        data,
        error: verifyError
      } = await supabase.auth.verifyOtp({
        phone: phoneNumber,
        token: code,
        type: 'sms'
      });
      if (verifyError) throw verifyError;
      if (data.user) {
        // For existing users, update validated status
        const {
          error: updateError
        } = await supabase.from('users').update({
          validated: true
        }).eq('phone_number', phoneNumber);
        
        if (updateError) throw updateError;
        
        // Clean up local storage if needed
        if (mode === 'signup') {
          localStorage.removeItem('pendingSignup');
        }
        
        toast.success('Successfully verified!');
        navigate('/home', {
          replace: true
        });
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      const errorMessage = error.message || error.error_description || 'Invalid verification code. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  if (!phoneNumber) {
    return null;
  }
  return <div className="min-h-screen flex flex-col items-center justify-center p-6 animate-fade-in">
      <div className="w-full max-w-sm flex flex-col items-center">
        <Logo variant="dark" />
        <h1 className="text-2xl font-bold mt-8 mb-2 text-center py-[32px]">Enter code</h1>
        
        
        <div className="w-full space-y-6">
          <Input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} placeholder="" value={code} onChange={e => setCode(e.target.value)} className="text-center text-2xl tracking-widest" />
          
          <Button className="w-full" size="lg" onClick={handleVerify} disabled={isLoading || code.length !== 6}>
            {isLoading ? 'Verifying...' : 'Verify'}
          </Button>

          <Button variant="ghost" className="w-full" onClick={handleResendCode} disabled={cooldownTime > 0}>
            {cooldownTime > 0 ? `Resend code (${cooldownTime}s)` : 'Resend code'}
          </Button>
        </div>
      </div>
    </div>;
};

export default Verify;
